import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ROUTE_CONSTANTS } from '@app/core/constants/route.constant';
import { getFreeSessions, isPaywallUser } from '@app/core/utils/free-trial.util';
import { IUserData } from '@app/modules/auth/interfaces/user-data.interface';
import { AuthApiService } from '@app/modules/auth/services/auth-api/auth-api.service';
import { PAYWALL_CONSTANTS } from '../constants/paywall.constants';

/** This visit (browser tab): the paywall already opened by itself. */
export const PAYWALL_SHOWN_THIS_VISIT_KEY = 'saideline.paywall.shownThisVisit';

/**
 * When the paywall opens by itself for a free coach: 10 seconds after they created their last free
 * session (the third), or when they then want to prompt again — at most once per visit, whatever the
 * reason. The exit intent (also once per coach) is in `ExitIntentService`.
 */
@Injectable({ providedIn: 'root' })
export class PaywallService {
    private isWatching = false;
    private userId: string | null = null;
    /** Free sessions the coach had used at the last update; null without a trial. */
    private usedSessions: number | null = null;
    private trialEndTimer?: ReturnType<typeof setTimeout>;
    /** Used when session storage is blocked, so the limit still holds until the next reload. */
    private shownThisVisit = false;

    constructor(
        private readonly router: Router,
        private readonly authService: AuthApiService
    ) {}

    /** Starts watching the free sessions. Called once when the app starts. */
    watchTrialEnd(): void {
        if (this.isWatching) {
            return;
        }
        this.isWatching = true;
        this.authService.user$.subscribe((user) => this.onUser(user));
    }

    /** A free coach who cannot generate another session: all free sessions used, or never had any. */
    isOutOfFreeSessions(user: IUserData | null = this.authService.currentUser): boolean {
        const trial = getFreeSessions(user);
        return isPaywallUser(user) && !(trial && trial.left > 0);
    }

    /** Whether the paywall may still open by itself in this visit. */
    get canOpen(): boolean {
        if (this.shownThisVisit) {
            return false;
        }
        try {
            return sessionStorage.getItem(PAYWALL_SHOWN_THIS_VISIT_KEY) !== 'true';
        } catch {
            return true;
        }
    }

    /**
     * Opens the paywall for this reason (`?source=`), unless it already opened in this visit.
     * @returns Whether it opened
     */
    open(source: string): boolean {
        if (!this.canOpen) {
            return false;
        }
        clearTimeout(this.trialEndTimer);
        this.shownThisVisit = true;
        try {
            sessionStorage.setItem(PAYWALL_SHOWN_THIS_VISIT_KEY, 'true');
        } catch {
            // Storage blocked: the in-memory flag applies
        }
        this.router.navigate(['/', ROUTE_CONSTANTS.UPGRADE], { queryParams: { source } });
        return true;
    }

    private onUser(user: IUserData | null): void {
        const trial = getFreeSessions(user);
        const used = trial ? trial.total - trial.left : null;
        const isSameCoach = !!user && user.sub === this.userId;

        if (!isSameCoach) {
            clearTimeout(this.trialEndTimer);
        }
        // The last free session was just created (not a coach who signs in with none left)
        if (isSameCoach && trial && this.usedSessions !== null && this.usedSessions < trial.total && trial.left === 0) {
            clearTimeout(this.trialEndTimer);
            this.trialEndTimer = setTimeout(() => this.openAfterTrialEnd(), PAYWALL_CONSTANTS.TRIAL_END_DELAY_MS);
        }

        this.userId = user?.sub ?? null;
        this.usedSessions = used;
    }

    private openAfterTrialEnd(): void {
        const segments = this.router.parseUrl(this.router.url).root.children['primary']?.segments ?? [];
        if (!this.isOutOfFreeSessions() || segments[0]?.path === ROUTE_CONSTANTS.UPGRADE) {
            return;
        }
        this.open(PAYWALL_CONSTANTS.SOURCES.TRIAL_END);
    }
}
